import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  Compass,
  ExternalLink,
  Sparkles,
  Settings,
  BookOpen,
  LogOut,
} from 'lucide-react';
import { AdminIcon } from '@/shared/config';
import { useAuthStore, useAdminAccess, useWikiAccess, keycloakLogout } from '@/features/auth';
import { useChangelogState } from '@/features/changelog';
import { useRevealStore, useAiAvailabilityStore } from '@/features/ai-assistant';
import { usePendingApprovalsStore } from '@/features/approvals';
import { useMessengerUnreadStore } from '@/features/messenger';
import { tourFor, useTourStore } from '@/features/onboarding-tour';
import { Avatar, Burger, Scroller, Tooltip, UnreadBadge } from '@/shared/ui';
import { useUiStore } from '@/shared/lib/uiStore';
import { useFocusTrap } from '@/shared/lib/useFocusTrap';
import { PORTAL_PRIMARY, type PortalNavLink } from '@/pages/MyTicketsPortal/nav-config';
import { cn } from '@/shared/lib/cn';
import styles from './Sidebar.module.css';

const ICON = 16;
const TRAIL_ICON = 12;

const TIP_OFFSET = 16;
const TIP_DELAY_RAIL = 520;
const TOGGLE_MS = 400;

function NavTip({ hint, meta }: { hint: string; meta?: string }) {
  return (
    <span className={styles.tip}>
      <span>{hint}</span>
      {meta && <span className={styles.tipMeta}>{meta}</span>}
    </span>
  );
}

function ContextualSection({
  isMobile,
  onNavigate,
}: {
  isMobile: boolean;
  onNavigate: () => void;
}) {
  const open = useRevealStore((s) => s.open);
  const aiAvailable = useAiAvailabilityStore((s) => s.available);
  const tipDelay = useUiStore((s) => s.sidebarCollapsed) ? TIP_DELAY_RAIL : undefined;

  if (!aiAvailable) return null;

  return (
    <section className={styles.group}>
      <ul className={styles.list}>
        <li>
          <Tooltip
            content={<NavTip hint={"Ответит по\u00a0базе знаний, не\u00a0уводя со\u00a0страницы"} />}
            side="right"
            className={styles.tipPopup}
            sideOffset={TIP_OFFSET}
            delay={tipDelay}
            disabled={isMobile}
          >
            <button
              type="button"
              className={cn(styles.navItem, styles.aiItem)}
              onClick={() => {
                open();
                onNavigate();
              }}
            >
              <Sparkles size={ICON} aria-hidden className={styles.aiItemIcon} />
              <span className={styles.aiItemLabel}>Спросить AI</span>
            </button>
          </Tooltip>
        </li>
      </ul>
    </section>
  );
}

function MenuVersion() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data } = useChangelogState(isAuthenticated);
  const version = data?.version ?? null;
  if (!version) return null;
  return (
    <Link to="/changelog" className={styles.version}>
      v{version}
    </Link>
  );
}

function UserSection({ onNavigate }: { onNavigate: () => void }) {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!user) return null;

  const display = user.name || user.email;

  return (
    <section className={cn(styles.group, styles.userSection)}>
      <div className={styles.userRow}>
        <Link to="/users/me" className={styles.userCard} onClick={onNavigate} data-tour="menu-profile">
          <Avatar className={styles.userAvatar} name={display} seed={user.id} src={user.avatarUrl} />
          <span className={styles.userText}>
            <span className={styles.userName}>{display}</span>
            {user.name && <span className={styles.userEmail}>{user.email}</span>}
          </span>
        </Link>

        <button
          type="button"
          className={styles.userAction}
          onClick={() => {
            onNavigate();
            useTourStore.getState().start(tourFor(hasPermission), true);
          }}
          aria-label="Экскурсия по порталу"
          title="Экскурсия по порталу"
          data-tour="menu-tour"
        >
          <Compass size={ICON} aria-hidden />
        </button>

        <Link
          to="/settings"
          className={styles.userAction}
          onClick={onNavigate}
          aria-label="Настройки"
          title="Настройки"
        >
          <Settings size={ICON} aria-hidden />
        </Link>

        <button
          type="button"
          className={styles.userAction}
          data-danger
          onClick={() => {
            onNavigate();
            keycloakLogout();
          }}
          aria-label="Выйти из аккаунта"
          title="Выйти из аккаунта"
        >
          <LogOut size={ICON} aria-hidden />
        </button>
      </div>

      <MenuVersion />
    </section>
  );
}

function SidebarNav({
  variant,
  onNavigate,
}: {
  variant: 'desktop' | 'mobile';
  onNavigate: () => void;
}) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isStaff = hasPermission('view_tickets');
  const wikiAccess = useWikiAccess();
  const tipDelay = useUiStore((s) => s.sidebarCollapsed) ? TIP_DELAY_RAIL : undefined;
  const { canAccess: canAccessAdmin } = useAdminAccess();
  const path = useLocation().pathname;
  const seg = /^\/tickets(?:\/([^/]+))?$/.exec(path)?.[1];
  const portalActive = path === '/tickets' || (seg != null && !['workspace', 'new', 'view'].includes(seg));
  const workspaceActive = path.startsWith('/tickets/workspace');
  const approvalsActive = path === '/approvals' || path.startsWith('/approvals/');
  const adminActive = path === '/admin' || path.startsWith('/admin/');
  const isActive = (to: string) =>
    to === '/tickets'
      ? portalActive
      : to === '/tickets/workspace'
        ? workspaceActive
        : to === '/approvals'
          ? approvalsActive
          : path === to;
  const pendingApprovals = usePendingApprovalsStore((s) => s.pending);
  const messengerUnread = useMessengerUnreadStore((s) => s.unread);

  const isMobile = variant === 'mobile';
  const allowed = PORTAL_PRIMARY.filter(
    (l) => (!l.staffOnly || isStaff) && (!l.permission || hasPermission(l.permission)),
  );
  const sections: PortalNavLink[] = [
    ...allowed.filter((l) => l.to),
    ...(wikiAccess
      ? [{ label: 'Инструкции', to: '/instructions', icon: BookOpen, hint: 'Статьи справочного центра' }]
      : []),
  ];
  const external = allowed.filter((l) => l.href);

  return (
    <nav className={styles.nav} aria-label="Навигация по разделам">
      <ContextualSection isMobile={isMobile} onNavigate={onNavigate} />

      <section className={styles.group}>
        <ul className={styles.list}>
          {sections.map((link) => {
            const Icon = link.icon;
            const to = link.to as string;
            const badge = link.approvalsBadge ? pendingApprovals : link.messengerBadge ? messengerUnread : 0;
            return (
              <li key={link.label}>
                <Tooltip
                  content={
                    <NavTip
                      hint={link.hint}
                      meta={
                        badge > 0
                          ? link.messengerBadge
                            ? `Непрочитанных:\u00a0${badge}`
                            : `Ждут сейчас:\u00a0${badge}`
                          : undefined
                      }
                    />
                  }
                  side="right"
                  className={styles.tipPopup}
                  sideOffset={TIP_OFFSET}
                  delay={tipDelay}
                  disabled={isMobile}
                >
                  <Link
                    to={to}
                    className={cn(styles.navItem, isActive(to) && styles.navItemActive)}
                    onClick={onNavigate}
                    data-tour={`nav:${to}`}
                  >
                    {Icon && <Icon size={ICON} aria-hidden className={styles.navIcon} />}
                    <span className={styles.navLabel}>{link.label}</span>
                    {badge > 0 && <span className={styles.navDot} aria-hidden />}
                    <UnreadBadge
                      count={badge}
                      className={styles.navBadge}
                      aria-label={link.messengerBadge ? `Непрочитанных: ${badge}` : `Ждут решения: ${badge}`}
                    />
                  </Link>
                </Tooltip>
              </li>
            );
          })}
          {canAccessAdmin && (
            <li>
              <Tooltip
                content={<NavTip hint={"Пользователи, права, флоу и\u00a0справочники"} />}
                side="right"
                className={styles.tipPopup}
                sideOffset={TIP_OFFSET}
                delay={tipDelay}
                disabled={isMobile}
              >
                <Link
                  to="/admin"
                  className={cn(styles.navItem, adminActive && styles.navItemActive)}
                  onClick={onNavigate}
                  data-tour="nav:/admin"
                >
                  <AdminIcon size={ICON} aria-hidden className={styles.navIcon} />
                  <span className={styles.navLabel}>Администрирование</span>
                </Link>
              </Tooltip>
            </li>
          )}
          {external.map((link) => {
            const Icon = link.icon;
            return (
              <li key={link.label}>
                <Tooltip
                  content={
                    <NavTip
                      hint={link.hint}
                      meta={`${new URL(link.href as string).host}\u00a0· в\u00a0новой вкладке`}
                    />
                  }
                  side="right"
                  className={styles.tipPopup}
                  sideOffset={TIP_OFFSET}
                  delay={tipDelay}
                  disabled={isMobile}
                >
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.navItem}
                    onClick={onNavigate}
                  >
                    {Icon && <Icon size={ICON} aria-hidden className={styles.navIcon} />}
                    <span className={styles.navLabel}>{link.label}</span>
                    <ExternalLink size={TRAIL_ICON} aria-hidden className={styles.navTrailIcon} />
                  </a>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </section>

      {isMobile && <UserSection onNavigate={onNavigate} />}
    </nav>
  );
}

export function Sidebar() {
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const menuRef = useRef<HTMLDivElement>(null);
  useFocusTrap(menuRef, mobileNavOpen);
  const setMobileNav = useUiStore((s) => s.setMobileNav);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const tourOnNav = useTourStore((s) => s.anchor?.startsWith('nav:') ?? false);
  const [prevCollapsed, setPrevCollapsed] = useState(collapsed);
  const [toggling, setToggling] = useState(false);
  if (prevCollapsed !== collapsed) {
    setPrevCollapsed(collapsed);
    setToggling(true);
  }
  useEffect(() => {
    if (!toggling) return;
    const t = window.setTimeout(() => setToggling(false), TOGGLE_MS);
    return () => window.clearTimeout(t);
  }, [toggling, collapsed]);
  const path = useLocation().pathname;
  const closeMenu = () => setMobileNav(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileNav(false);
  }, [path, setMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileNav(false);
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen, setMobileNav]);

  useEffect(() => {
    if (mobileNavOpen) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
      toggleRef.current?.focus();
      return;
    }
    restoreFocusRef.current?.focus?.();
    restoreFocusRef.current = null;
  }, [mobileNavOpen]);

  return (
    <>
      <Scroller
        element="aside"
        className={cn(
          styles.sidebar,
          collapsed && styles.sidebarRail,
          toggling && styles.sidebarToggling,
        )}
        viewportClassName={styles.sidebarViewport}
        data-tour-open={collapsed && tourOnNav ? '' : undefined}
      >
        <SidebarNav variant="desktop" onNavigate={closeMenu} />
      </Scroller>

      {createPortal(
        <div
          ref={menuRef}
          className={cn(
            styles.mobileMenu,
            mobileNavOpen && styles.mobileMenuOpen,
          )}
          role="dialog"
          aria-modal="true"
          aria-label="Меню"
          aria-hidden={!mobileNavOpen}
        >
          <div className={styles.mobileBar}>
            <button
              ref={toggleRef}
              type="button"
              className={styles.barToggle}
              onClick={closeMenu}
              aria-label="Закрыть меню"
              aria-expanded
            >
              <Burger open />
            </button>
          </div>
          <Scroller
            className={styles.mobileScroll}
            viewportClassName={styles.mobileScrollViewport}
          >
            <SidebarNav variant="mobile" onNavigate={closeMenu} />
          </Scroller>
        </div>,
        document.body,
      )}
    </>
  );
}
